import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiDollarSign, FiPercent, FiCalculator } = FiIcons;

const CommissionModal = ({ isOpen, client, onClose, onSubmit }) => {
  const [commissionType, setCommissionType] = useState('amount');
  const [commissionValue, setCommissionValue] = useState('');
  const [calculatedAmount, setCalculatedAmount] = useState(0);

  useEffect(() => {
    if (client && commissionType === 'percentage' && commissionValue) {
      const percentage = parseFloat(commissionValue) || 0;
      const amount = (client.contractValue * percentage) / 100;
      setCalculatedAmount(amount);
    } else if (commissionType === 'amount') {
      setCalculatedAmount(parseFloat(commissionValue) || 0);
    }
  }, [commissionValue, commissionType, client]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalAmount = commissionType === 'percentage' 
      ? calculatedAmount 
      : parseFloat(commissionValue) || 0;
    
    if (finalAmount > 0) {
      onSubmit(finalAmount);
      setCommissionValue('');
      setCalculatedAmount(0);
    }
  };

  const handleClose = () => {
    setCommissionValue('');
    setCalculatedAmount(0);
    setCommissionType('amount');
    onClose();
  };

  if (!client) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black bg-opacity-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Registra Commissione</h2>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {/* Client Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-1">{client.contactName}</h3>
              <p className="text-sm text-gray-600 mb-2">{client.clientName}</p>
              <p className="text-lg font-semibold text-emerald-600">
                Valore Contratto: €{client.contractValue.toLocaleString()}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Commission Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Commissione
                </label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setCommissionType('amount')}
                    className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-colors ${
                      commissionType === 'amount'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={FiDollarSign} className="inline mr-1" />
                    Importo Fisso
                  </button>
                  <button
                    type="button"
                    onClick={() => setCommissionType('percentage')}
                    className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-colors ${
                      commissionType === 'percentage'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={FiPercent} className="inline mr-1" />
                    Percentuale
                  </button>
                </div>
              </div>

              {/* Commission Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {commissionType === 'amount' ? 'Importo Commissione (€)' : 'Percentuale (%)'}
                </label>
                <div className="relative">
                  <SafeIcon 
                    icon={commissionType === 'amount' ? FiDollarSign : FiPercent} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                  />
                  <input
                    type="number"
                    min="0"
                    step={commissionType === 'amount' ? '0.01' : '0.1'}
                    max={commissionType === 'percentage' ? '100' : undefined}
                    value={commissionValue}
                    onChange={(e) => setCommissionValue(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder={commissionType === 'amount' ? '0.00' : '0.0'}
                    required
                  />
                </div>
              </div>

              {/* Calculated Amount */}
              {commissionType === 'percentage' && commissionValue && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCalculator} className="text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">
                      Commissione Calcolata: €{calculatedAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Registra Commissione
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CommissionModal;